import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiShoppingCart,
  FiTruck,
  FiRefreshCcw,
  FiMessageCircle,
  FiArrowLeft,
  FiStar
} from "react-icons/fi";
import axios from "axios";
import "./productDetails.css";

const API_BASE = "https://api.thetotetherapy.com";

function Stars({ rating = 3, outOf = 4 }) {
  return (
    <div className="stars" aria-label={`${rating} out of ${outOf} stars`}>
      {Array.from({ length: outOf }).map((_, i) => (
        <span key={i} className={i < rating ? "star filled" : "star"}>
           <FiStar />
        </span>
      ))}
      <span className="starsText">{rating}/{outOf}</span>
    </div>
  );
}

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");

  // dummy reviews (later API se laa lena)
  const reviews = useMemo(
    () => [
      {
        name: "Ayesha",
        date: "Feb 2026",
        text: "The tote quality is amazing and stitching is premium. Loved it!",
        rating: 3,
      },
      {
        name: "Sara",
        date: "Jan 2026",
        text: "Very cute design. Fabric feels strong and reusable.",
        rating: 3,
      },
      {
        name: "Hina",
        date: "Dec 2025",
        text: "Perfect for daily use. Looks even better in real life.",
        rating: 3,
      },
    ],
    []
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setErr("");

        const [productRes, productsRes] = await Promise.all([
          axios.get(`${API_BASE}/api/products/${id}`),
          axios.get(`${API_BASE}/api/products`),
        ]);

        setProduct(productRes.data);
        setAllProducts(productsRes.data);
      } catch (e) {
        setErr(e.response?.data?.message || "Product not found");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);
const handleAddToCart = () => {
  const existingCart = JSON.parse(localStorage.getItem("cart")) || [];

  const found = existingCart.find((item) => item.id === product.id);

  if (found) {
    found.quantity += 1;
  } else {
    existingCart.push({
      id: product.id,
      title: product.title,
      price: Number(product.price_aed),
      image: product.image_url,
      quantity: 1,
    });
  }

  localStorage.setItem("cart", JSON.stringify(existingCart));

  setToast(`${product.title} added to cart ✅`);
};
useEffect(() => {
  if (!toast) return;
  const t = setTimeout(() => setToast(""), 2000);
  return () => clearTimeout(t);
}, [toast]);

  const relatedProducts = useMemo(() => {
    // simple related logic: same list se current product hata ke 4 show
    return allProducts
      .filter((p) => String(p.id) !== String(id))
      .slice(0, 4);
  }, [allProducts, id]);

  if (loading) return <div className="detailsState">Loading...</div>;
  if (err) return <div className="detailsState error">{err}</div>;
  if (!product) return null;

  return (
    <main className="detailsPage">
      {toast && (
  <div className="toast">
    {toast}
    <button onClick={() => setToast("")}>✕</button>
  </div>
)}
      <div className="detailsShell">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <span onClick={() => navigate("/shop")} className="crumbLink">
            Shop
          </span>
          <span className="crumbSep">/</span>
          <span className="crumbCurrent">{product.title}</span>
        </div>

        {/* Top Section */}
        <section className="detailsTop">
          {/* Gallery */}
          <div className="detailsGallery">
            <div className="mainImage">
              {product.image_url ? (
                <img
                  src={`${API_BASE}${product.image_url}`}
                  alt={product.title}
                />
              ) : (
                <div className="imgPlaceholder">No Image</div>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="detailsInfo">
            <h1 className="detailsTitle">{product.title}</h1>

            <div className="detailsRow">
              <Stars rating={3} outOf={4} />
              <span className="mutedText">({reviews.length} reviews)</span>
            </div>

            <p className="detailsPrice">
              AED {Number(product.price_aed).toFixed(2)}
            </p>
<div className="codBadge">
  🚚 Cash on Delivery Available Across UAE
</div>
            <p className="detailsDescription">
              {product.description ||
                "A thoughtfully designed tote created for everyday calm — blending purpose, durability, and mindful living into one essential piece."}
            </p>

            <div className="detailsBadges">
              <span className="badge">Sustainable</span>
              <span className="badge">Premium Quality</span>
              <span className="badge">Mindfully Made</span>
            </div>

            <div className="detailsActions">
              <button className="addToCartBtn" onClick={handleAddToCart}>
  <FiShoppingCart /> Add to Cart
</button>
              <button className="secondaryBtn" onClick={() => navigate("/shop")}>
              <FiArrowLeft />   Back to Shop
              </button>
            </div>

            <div className="miniInfo">
  <div className="miniInfoItem">
    <FiTruck className="miniIcon" />
    <div>
      <span className="miniLabel">Delivery</span>
      <span className="miniValue">2–4 working days</span>
    </div>
  </div>

  <div className="miniInfoItem">
    <FiRefreshCcw className="miniIcon" />
    <div>
      <span className="miniLabel">Final Sale</span>
      <span className="miniValue">Exchanges for defects only</span>
    </div>
  </div>

  <div className="miniInfoItem">
    <FiMessageCircle className="miniIcon" />
    <div>
      <span className="miniLabel">Support</span>
      <span className="miniValue">WhatsApp available</span>
    </div>
  </div>
</div>
          </div>
        </section>

        {/* Reviews */}
        <section className="detailsSection">
          <div className="sectionHeader">
            <h2>Customer Reviews</h2>
            <div className="sectionRight">
              <Stars rating={3} outOf={4} />
            </div>
          </div>

          <div className="reviewGrid">
            {reviews.map((r, idx) => (
              <article className="reviewCard" key={idx}>
                <div className="reviewTop">
                  <div className="reviewAvatar">{r.name[0]}</div>
                  <div className="reviewMeta">
                    <div className="reviewName">{r.name}</div>
                    <div className="reviewDate">{r.date}</div>
                  </div>
                  <div className="reviewStars">
                    <Stars rating={r.rating} outOf={4} />
                  </div>
                </div>
                <p className="reviewText">{r.text}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Related products */}
        <section className="detailsSection">
          <div className="sectionHeader">
            <h2>You might also like</h2>
            <p className="mutedText">More picks from our collection</p>
          </div>

          {relatedProducts.length === 0 ? (
            <div className="detailsState">No related products.</div>
          ) : (
            <div className="relatedGrid">
              {relatedProducts.map((p) => (
                <article
                  className="relatedCard"
                  key={p.id}
                  onClick={() => navigate(`/product/${p.id}`)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="relatedImg">
                    {p.image_url ? (
                      <img
                        src={`${API_BASE}${p.image_url}`}
                        alt={p.title}
                      />
                    ) : (
                      <div className="imgPlaceholder small">No Image</div>
                    )}
                  </div>
                  <div className="relatedBody">
                    <h3 className="relatedTitle">{p.title}</h3>
                    <div className="relatedMeta">
                      <Stars rating={3} outOf={4} />
                      <span className="relatedPrice">
                        AED {Number(p.price_aed).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}